/** Response envelope + error codes — spec §2.4 and §7.9. */
import { z } from 'zod';
/** Stable, screaming-snake error codes. HTTP status is attached server-side. */
export const ERROR_CODES = {
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    DUPLICATE_KEY: 'DUPLICATE_KEY',
    STALE_WRITE: 'STALE_WRITE',
    DEPENDENCY_CYCLE: 'DEPENDENCY_CYCLE',
    DEPENDENCY_UNRESOLVED: 'DEPENDENCY_UNRESOLVED',
    WIP_LIMIT_EXCEEDED: 'WIP_LIMIT_EXCEEDED',
    TIMER_ALREADY_RUNNING: 'TIMER_ALREADY_RUNNING',
    PROJECT_REQUIRED: 'PROJECT_REQUIRED',
    PROJECT_ARCHIVED: 'PROJECT_ARCHIVED',
    SPRINT_ALREADY_ACTIVE: 'SPRINT_ALREADY_ACTIVE',
    LEAVE_OVERLAP: 'LEAVE_OVERLAP',
    OWNER_ROLE_IMMUTABLE: 'OWNER_ROLE_IMMUTABLE',
    PHONE_UNVERIFIED: 'PHONE_UNVERIFIED',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
    TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    INVITE_INVALID: 'INVITE_INVALID',
    TASK_DEPTH_EXCEEDED: 'TASK_DEPTH_EXCEEDED',
    STATUS_HAS_TASKS: 'STATUS_HAS_TASKS',
    CONFIRMATION_MISMATCH: 'CONFIRMATION_MISMATCH',
    UPLOAD_REJECTED: 'UPLOAD_REJECTED',
    BOOTSTRAP_ALREADY_DONE: 'BOOTSTRAP_ALREADY_DONE',
    SPRINT_NOT_EMPTY: 'SPRINT_NOT_EMPTY',
    TASK_LIMIT_EXCEEDED: 'TASK_LIMIT_EXCEEDED',
    EDIT_WINDOW_EXPIRED: 'EDIT_WINDOW_EXPIRED',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    ORIGIN_NOT_ALLOWED: 'ORIGIN_NOT_ALLOWED',
    // V3 R5 — multi-company SaaS.
    STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',
    /** No object store resolved for this workspace — see `workspaceStorage.ts`. */
    STORAGE_NOT_CONFIGURED: 'STORAGE_NOT_CONFIGURED',
    WORKSPACE_SUSPENDED: 'WORKSPACE_SUSPENDED',
    SIGNUPS_DISABLED: 'SIGNUPS_DISABLED',
    EMAIL_TAKEN: 'EMAIL_TAKEN',
    /**
     * V10 §1 — starting work while a session is still open.
     *
     * Its own code because the client has a *remedy* for this one and none for a
     * generic validation failure: "End previous session & start". A message the
     * UI cannot act on is how this became a dead end in the first place.
     */
    ATTENDANCE_SESSION_OPEN: 'ATTENDANCE_SESSION_OPEN',
    /**
     * V10 §5 — the row exists, the bytes do not.
     *
     * Its own code so the UI can say "File unavailable" instead of spinning:
     * attachments uploaded before the bucket switch still live in the old bucket
     * and cannot be served until the copy runs. That is a fact about storage, not
     * a permission problem and not a missing record.
     */
    STORAGE_OBJECT_MISSING: 'STORAGE_OBJECT_MISSING',
};
/** ObjectId-shaped string. */
export const objectIdSchema = z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, 'Must be a 24-character hex id');
export const isoDateSchema = z
    .string()
    .datetime({ offset: true })
    .or(z.string().regex(/^\d{4}-\d{2}-\d{2}$/));
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Comma-separated query values, e.g. `?priority=P0,P1`. */
export const csvOf = (inner) => z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : v.split(',')))
    .transform((v) => v.map((s) => s.trim()).filter(Boolean))
    .pipe(z.array(inner));
export const hexColourSchema = z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex colour');
/** TipTap document — validated structurally, sanitised server-side (§11.10). */
export const richTextSchema = z.object({
    type: z.string(),
    content: z.array(z.any()).optional(),
    attrs: z.record(z.any()).optional(),
    marks: z.array(z.any()).optional(),
    text: z.string().optional(),
}).passthrough();
//# sourceMappingURL=common.js.map